import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Popconfirm, message, Card, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { measureNewsApi } from '../../services/api';

const { Title } = Typography;

const MeasureNewsList = () => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const navigate = useNavigate();

  const fetchNewsList = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await measureNewsApi.getList({ page });
      const processedRows = (response.data.rows || []).map(item => ({
        ...item,
        title: item.title ? decodeURIComponent(item.title) : 'タイトルなし',
        content: item.content ? decodeURIComponent(item.content) : '内容なし'
      }));
      setNewsList(processedRows);
      setPagination(prev => ({
        ...prev,
        current: page,
        total: response.data.count || 0,
      }));
    } catch (error) {
      message.error('Measureニュース一覧の取得に失敗しました：' + (error.response?.data?.message || error.message || 'ネットワークエラー'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNewsList(1);
  }, [fetchNewsList]);
  
const handleTableChange = (newPagination) => {
  const nextPage = newPagination.current;
  setPagination(prev => ({
    ...prev,
    current: nextPage,
  }));
  fetchNewsList(nextPage);
};

  const handleDelete = async (id) => {
    try {
      await measureNewsApi.delete(id);
      message.success('削除しました');
      fetchNewsList(pagination.current);
    } catch (error) {
      message.error('削除に失敗しました：' + (error.response?.data?.message || 'ネットワークエラー'));
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'タイトル',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'ソート',
      dataIndex: 'sort',
      key: 'sort',
      width: 100,
    },
    {
      title: '作成日時',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 200,
      render: (text) => new Date(text).toLocaleString('ja-JP'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/measure-news/edit/${record.id}`)}
          >
            編集
          </Button>
          <Popconfirm
            title="削除確認"
            description="このMeasureニュースを削除してもよろしいですか？"
            onConfirm={() => handleDelete(record.id)}
            okText="はい"
            cancelText="いいえ"
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              削除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>Measureニュース一覧</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/measure-news/create')}
        >
          Measureニュース作成
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={newsList}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showQuickJumper: true,
        }}
        onChange={handleTableChange}
        rowKey="id"
      />
    </Card>
  );
};

export default MeasureNewsList;
